import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {User, UserType} from '../services/api.client';
import {SecurityService} from '../services/SecurityService';

/**
 * Route-level authorization guard. Mirrors the role-based visibility rules that the
 * sidebar (SidebarComponent.html) already applies to the navigation links, so that a
 * page cannot be reached simply by typing its URL when the corresponding nav entry
 * would be hidden for the current user's role.
 *
 * The allowed roles are read verbatim from each route's `data.roles` array — this guard
 * deliberately does NOT introduce any new access logic; it only enforces the existing
 * sidebar conditions at the router. It is defense-in-depth on top of the server-side
 * authorization, not a replacement for it.
 *
 * Fail-closed: a route with a missing/empty `roles` list denies everyone. The parent
 * `app` route's AuthGuard has already guaranteed an authenticated user by the time this
 * guard runs, so a null user here simply falls through to the not-authorized redirect.
 */
@Injectable()
export class RoleGuard {

    private user$: Observable<User>;

    constructor(
        private router: Router,
        private _securityService: SecurityService) {
        this.user$ = this._securityService.currentUser$;
    }

    canActivate(route: ActivatedRouteSnapshot,
                state: RouterStateSnapshot): Observable<boolean> {
        const allowedRoles: UserType[] = (route.data && route.data['roles']) || [];
        return this.user$.pipe(
            take(1),
            map((user) => {
                if (user && allowedRoles.indexOf(user.userType) !== -1) {
                    return true;
                }
                this.router.navigate(['/not-authorized']).then();
                return false;
            }));
    }
}
